import React, { useState } from 'react';
import { Download, GitBranch, GitMerge, Users, FileText, Table } from 'lucide-react';

const GitBranchingDocs = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const generatePDF = () => {
    const content = document.getElementById('pdf-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Git Branching Strategy - Documentation</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
            h1 { color: #2563eb; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
            h2 { color: #1e40af; margin-top: 30px; border-bottom: 2px solid #93c5fd; padding-bottom: 8px; }
            h3 { color: #1e3a8a; margin-top: 20px; }
            .branch-flow { background: #eff6ff; padding: 20px; border-radius: 8px; margin: 20px 0; }
            .step { background: #dbeafe; padding: 15px; margin: 10px 0; border-left: 4px solid #2563eb; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th { background: #2563eb; color: white; padding: 12px; text-align: left; }
            td { border: 1px solid #ddd; padding: 10px; }
            tr:nth-child(even) { background: #f3f4f6; }
            .example { background: #fef3c7; padding: 15px; border-radius: 5px; margin: 10px 0; }
            .note { background: #fef2f2; border-left: 4px solid #ef4444; padding: 15px; margin: 10px 0; }
            code { background: #f3f4f6; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace; }
            @media print {
              .no-print { display: none; }
              body { margin: 20px; }
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const generateCSV = () => {
    const csvData = [
      ['Branch Name', 'Branch Type', 'Parent Branch', 'Purpose', 'Merge To', 'Who Works'],
      ['main', 'Production', '-', 'Production-ready code', '-', 'Release Manager'],
      ['sandbox', 'Pre-production', 'main', 'Testing before production', 'main', 'QA Team'],
      ['develop', 'Integration', 'sandbox', 'Integration of all modules', 'sandbox', 'Tech Lead'],
      ['develop_challan', 'Module', 'develop', 'Challan module development', 'develop', 'Module Lead'],
      ['develop_challan_swapnali', 'Feature', 'develop_challan', 'Individual feature work', 'develop_challan', 'Swapnali'],
      ['develop_challan_aniket', 'Feature', 'develop_challan', 'Individual feature work', 'develop_challan', 'Aniket'],
      ['develop_grievances', 'Module', 'develop', 'Grievances module development', 'develop', 'Module Lead'],
      ['develop_grievances_divya', 'Feature', 'develop_grievances', 'Individual feature work', 'develop_grievances', 'Divya'],
      ['develop_grievances_anish', 'Feature', 'develop_grievances', 'Individual feature work', 'develop_grievances', 'Anish'],
      ['develop_inspection', 'Module', 'develop', 'Inspection module development', 'develop', 'Module Lead'],
      ['develop_inspection_abhijeet', 'Feature', 'develop_inspection', 'Individual feature work', 'develop_inspection', 'Abhijeet'],
      ['develop_scp_challan', 'Module', 'develop', 'SCP Challan module', 'develop', 'Module Lead'],
      ['develop_sms', 'Module', 'develop', 'SMS module', 'develop', 'Module Lead'],
      ['release_challan_1.0.0', 'Release', 'sandbox', 'Challan module release v1.0.0', 'main', 'Release Manager'],
      ['release_grievances_1.0.0', 'Release', 'sandbox', 'Grievances module release v1.0.0', 'main', 'Release Manager']
    ];

    const csv = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'git_branching_strategy.csv';
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white">
            <div className="flex items-center gap-4">
              <GitBranch size={48} />
              <div>
                <h1 className="text-4xl font-bold mb-2">Git Branching Strategy</h1>
                <p className="text-blue-100">Module-Based Development Workflow</p>
              </div>
            </div>
          </div>

          <div className="flex border-b bg-gray-50">
            {['overview', 'workflow', 'branches', 'commands'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-semibold transition-colors ${
                  activeTab === tab
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-blue-600'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="p-8">
            <div className="flex gap-4 mb-6">
              <button
                onClick={generatePDF}
                className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors shadow-md"
              >
                <Download size={20} />
                Export as PDF
              </button>
              <button
                onClick={generateCSV}
                className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors shadow-md"
              >
                <Table size={20} />
                Export as CSV
              </button>
            </div>

            <div id="pdf-content">
              {activeTab === 'overview' && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Overview</h2>
                  
                  <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6 rounded">
                    <h3 className="text-xl font-bold text-blue-800 mb-3">Branch Hierarchy</h3>
                    <pre className="text-sm bg-white p-4 rounded overflow-x-auto font-mono">
{`main
 └── sandbox
      └── develop
           ├── develop_challan
           │    ├── develop_challan_swapnali
           │    └── develop_challan_aniket
           │
           ├── develop_grievances
           │    ├── develop_grievances_divya
           │    └── develop_grievances_anish
           │
           ├── develop_inspection
           │    └── develop_inspection_abhijeet
           │
           ├── develop_scp_challan
           └── develop_sms`}
                    </pre>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow">
                      <h3 className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
                        <GitBranch size={24} />
                        Core Branches
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li><strong>main:</strong> Production code</li>
                        <li><strong>sandbox:</strong> Pre-production testing</li>
                        <li><strong>develop:</strong> Integration branch</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-lg shadow">
                      <h3 className="text-xl font-bold text-purple-800 mb-3 flex items-center gap-2">
                        <Users size={24} />
                        Module Branches
                      </h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>develop_challan</li>
                        <li>develop_grievances</li>
                        <li>develop_inspection</li>
                        <li>develop_scp_challan</li>
                        <li>develop_sms</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded">
                    <h3 className="text-xl font-bold text-yellow-800 mb-3">Key Principles</h3>
                    <ul className="space-y-2 text-gray-700 list-disc list-inside">
                      <li>Developers work on individual feature branches</li>
                      <li>Merge requests flow from individual → module → develop → sandbox</li>
                      <li>Release branches created from sandbox with version naming</li>
                      <li>Tested releases merged to main</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'workflow' && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Development Workflow</h2>

                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 1: Individual Development</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-2">Developer creates feature branch from module branch:</p>
                        <code className="block bg-black bg-opacity-30 p-3 rounded">
                          git checkout develop_challan<br/>
                          git pull origin develop_challan<br/>
                          git checkout -b develop_challan_swapnali
                        </code>
                        <p className="mt-3">Developer works on their feature and commits regularly.</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 2: Merge to Module Branch</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-2">After completion, create Merge Request:</p>
                        <p><strong>From:</strong> develop_challan_swapnali</p>
                        <p><strong>To:</strong> develop_challan</p>
                        <p className="mt-3">Module lead reviews and approves the merge.</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 3: Merge to Develop</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-2">Module lead creates Merge Request:</p>
                        <p><strong>From:</strong> develop_challan</p>
                        <p><strong>To:</strong> develop</p>
                        <p className="mt-3">Tech lead reviews integration and approves.</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 4: Merge to Sandbox</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-2">Tech lead creates Merge Request:</p>
                        <p><strong>From:</strong> develop</p>
                        <p><strong>To:</strong> sandbox</p>
                        <p className="mt-3">QA team tests in sandbox environment.</p>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 5: Create Release Branch</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-2">When ready for release, create release branch from sandbox:</p>
                        <code className="block bg-black bg-opacity-30 p-3 rounded">
                          git checkout sandbox<br/>
                          git pull origin sandbox<br/>
                          git checkout -b release_challan_1.0.0
                        </code>
                        <p className="mt-3"><strong>Version Format:</strong> module_MAJOR.MINOR.PATCH</p>
                        <ul className="list-disc list-inside mt-2 ml-4">
                          <li>First release: 1.0.0</li>
                          <li>Bug fixes: 1.0.1, 1.0.2</li>
                          <li>Minor features: 1.1.0, 1.2.0</li>
                          <li>Major changes: 2.0.0</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 6: Test and Deploy</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-3">Release branch undergoes thorough testing:</p>
                        <ul className="list-disc list-inside ml-4 space-y-1">
                          <li>QA performs full regression testing</li>
                          <li>Bug fixes committed to release branch</li>
                          <li>UAT (User Acceptance Testing)</li>
                          <li>Performance testing</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-lg shadow-lg">
                      <h3 className="text-2xl font-bold mb-3">Step 7: Merge to Main</h3>
                      <div className="bg-white bg-opacity-20 p-4 rounded">
                        <p className="mb-2">After successful testing, merge to production:</p>
                        <p><strong>From:</strong> release_challan_1.0.0</p>
                        <p><strong>To:</strong> main</p>
                        <p className="mt-3">Tag the release in main branch:</p>
                        <code className="block bg-black bg-opacity-30 p-3 rounded mt-2">
                          git tag -a v1.0.0_challan -m "Challan Module Release 1.0.0"<br/>
                          git push origin v1.0.0_challan
                        </code>
                        <p className="mt-3 bg-red-700 bg-opacity-50 p-3 rounded">
                          <strong>Important:</strong> Also merge release branch back to develop and sandbox to keep them updated!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'branches' && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Branch Reference Table</h2>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse bg-white shadow-lg rounded-lg overflow-hidden">
                      <thead>
                        <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                          <th className="p-4 text-left">Branch Name</th>
                          <th className="p-4 text-left">Type</th>
                          <th className="p-4 text-left">Parent</th>
                          <th className="p-4 text-left">Purpose</th>
                          <th className="p-4 text-left">Merge To</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-4 font-mono font-bold text-red-600">main</td>
                          <td className="p-4">Production</td>
                          <td className="p-4">-</td>
                          <td className="p-4">Production-ready code</td>
                          <td className="p-4">-</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-gray-50">
                          <td className="p-4 font-mono font-bold text-orange-600">sandbox</td>
                          <td className="p-4">Pre-prod</td>
                          <td className="p-4">main</td>
                          <td className="p-4">Pre-production testing</td>
                          <td className="p-4">main</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-4 font-mono font-bold text-yellow-600">develop</td>
                          <td className="p-4">Integration</td>
                          <td className="p-4">sandbox</td>
                          <td className="p-4">Module integration</td>
                          <td className="p-4">sandbox</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-blue-50">
                          <td className="p-4 font-mono text-blue-600">develop_challan</td>
                          <td className="p-4">Module</td>
                          <td className="p-4">develop</td>
                          <td className="p-4">Challan module development</td>
                          <td className="p-4">develop</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-4 font-mono text-sm">develop_challan_swapnali</td>
                          <td className="p-4">Feature</td>
                          <td className="p-4">develop_challan</td>
                          <td className="p-4">Individual feature work</td>
                          <td className="p-4">develop_challan</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-gray-50">
                          <td className="p-4 font-mono text-sm">develop_challan_aniket</td>
                          <td className="p-4">Feature</td>
                          <td className="p-4">develop_challan</td>
                          <td className="p-4">Individual feature work</td>
                          <td className="p-4">develop_challan</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-purple-50">
                          <td className="p-4 font-mono text-purple-600">develop_grievances</td>
                          <td className="p-4">Module</td>
                          <td className="p-4">develop</td>
                          <td className="p-4">Grievances module</td>
                          <td className="p-4">develop</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-4 font-mono text-sm">develop_grievances_divya</td>
                          <td className="p-4">Feature</td>
                          <td className="p-4">develop_grievances</td>
                          <td className="p-4">Individual feature work</td>
                          <td className="p-4">develop_grievances</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-gray-50">
                          <td className="p-4 font-mono text-sm">develop_grievances_anish</td>
                          <td className="p-4">Feature</td>
                          <td className="p-4">develop_grievances</td>
                          <td className="p-4">Individual feature work</td>
                          <td className="p-4">develop_grievances</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-green-50">
                          <td className="p-4 font-mono text-green-600">develop_inspection</td>
                          <td className="p-4">Module</td>
                          <td className="p-4">develop</td>
                          <td className="p-4">Inspection module</td>
                          <td className="p-4">develop</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50">
                          <td className="p-4 font-mono text-sm">develop_inspection_abhijeet</td>
                          <td className="p-4">Feature</td>
                          <td className="p-4">develop_inspection</td>
                          <td className="p-4">Individual feature work</td>
                          <td className="p-4">develop_inspection</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-pink-50">
                          <td className="p-4 font-mono text-pink-600">release_challan_1.0.0</td>
                          <td className="p-4">Release</td>
                          <td className="p-4">sandbox</td>
                          <td className="p-4">Challan release v1.0.0</td>
                          <td className="p-4">main</td>
                        </tr>
                        <tr className="border-b hover:bg-gray-50 bg-gray-50">
                          <td className="p-4 font-mono text-pink-600">release_challan_1.0.1</td>
                          <td className="p-4">Release</td>
                          <td className="p-4">sandbox</td>
                          <td className="p-4">Challan release v1.0.1</td>
                          <td className="p-4">main</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-8 bg-amber-50 border-l-4 border-amber-500 p-6 rounded">
                    <h3 className="text-xl font-bold text-amber-800 mb-3">Naming Conventions</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li><strong>Module branches:</strong> develop_[module_name]</li>
                      <li><strong>Feature branches:</strong> develop_[module]_[developer]</li>
                      <li><strong>Release branches:</strong> release_[module]_[version]</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'commands' && (
                <div>
                  <h2 className="text-3xl font-bold text-gray-800 mb-6">Git Commands Reference</h2>

                  <div className="space-y-6">
                    <div className="bg-white border-2 border-blue-200 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-blue-800 mb-4">Developer: Starting New Feature</h3>
                      <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm">
                        <div># Switch to module branch</div>
                        <div>git checkout develop_challan</div>
                        <div className="mt-2"># Get latest changes</div>
                        <div>git pull origin develop_challan</div>
                        <div className="mt-2"># Create your feature branch</div>
                        <div>git checkout -b develop_challan_swapnali</div>
                        <div className="mt-2"># Make changes and commit</div>
                        <div>git add .</div>
                        <div>git commit -m "feat: add challan validation"</div>
                        <div className="mt-2"># Push to remote</div>
                        <div>git push -u origin develop_challan_swapnali</div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-purple-200 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-purple-800 mb-4">Developer: Creating Merge Request</h3>
                      <div className="bg-gray-100 p-4 rounded">
                        <p className="mb-3">After pushing your changes:</p>
                        <ol className="list-decimal list-inside space-y-2">
                          <li>Go to your Git repository (GitLab/GitHub/Bitbucket)</li>
                          <li>Navigate to Merge Requests / Pull Requests</li>
                          <li>Click "New Merge Request"</li>
                          <li>Set source: develop_challan_swapnali</li>
                          <li>Set target: develop_challan</li>
                          <li>Add description of changes</li>
                          <li>Assign to module lead for review</li>
                          <li>Submit merge request</li>
                        </ol>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-green-200 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-green-800 mb-4">Module Lead: Reviewing and Merging</h3>
                      <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm">
                        <div># Review changes locally</div>
                        <div>git fetch origin</div>
                        <div>git checkout develop_challan_swapnali</div>
                        <div>git pull origin develop_challan_swapnali</div>
                        <div className="mt-2"># Test the changes</div>
                        <div># Run tests, build, etc.</div>
                        <div className="mt-2"># If approved, merge via UI or:</div>
                        <div>git checkout develop_challan</div>
                        <div>git merge --no-ff develop_challan_swapnali</div>
                        <div>git push origin develop_challan</div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-orange-200 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-orange-800 mb-4">Release Manager: Creating Release</h3>
                      <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm">
                        <div># Ensure sandbox is up to date</div>
                        <div>git checkout sandbox</div>
                        <div>git pull origin sandbox</div>
                        <div className="mt-2"># Create release branch</div>
                        <div>git checkout -b release_challan_1.0.0</div>
                        <div>git push -u origin release_challan_1.0.0</div>
                        <div className="mt-2"># After testing, merge to main</div>
                        <div>git checkout main</div>
                        <div>git merge --no-ff release_challan_1.0.0</div>
                        <div className="mt-2"># Tag the release</div>
                        <div>git tag -a v1.0.0_challan -m "Challan Release 1.0.0"</div>
                        <div>git push origin main --tags</div>
                        <div className="mt-2"># Merge back to develop and sandbox</div>
                        <div>git checkout develop</div>
                        <div>git merge release_challan_1.0.0</div>
                        <div>git push origin develop</div>
                        <div className="mt-2">git checkout sandbox</div>
                        <div>git merge release_challan_1.0.0</div>
                        <div>git push origin sandbox</div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-red-200 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-red-800 mb-4">Keeping Your Branch Updated</h3>
                      <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm">
                        <div># Update your feature branch with latest module changes</div>
                        <div>git checkout develop_challan</div>
                        <div>git pull origin develop_challan</div>
                        <div className="mt-2">git checkout develop_challan_swapnali</div>
                        <div>git merge develop_challan</div>
                        <div className="mt-2"># Or use rebase (cleaner history)</div>
                        <div>git rebase develop_challan</div>
                        <div className="mt-2"># Push updated branch</div>
                        <div>git push origin develop_challan_swapnali</div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-indigo-200 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-indigo-800 mb-4">Useful Git Commands</h3>
                      <div className="bg-gray-900 text-green-400 p-4 rounded font-mono text-sm">
                        <div># View all branches</div>
                        <div>git branch -a</div>
                        <div className="mt-2"># Check current branch</div>
                        <div>git branch</div>
                        <div className="mt-2"># View commit history</div>
                        <div>git log --oneline --graph --decorate</div>
                        <div className="mt-2"># Check status</div>
                        <div>git status</div>
                        <div className="mt-2"># View changes</div>
                        <div>git diff</div>
                        <div className="mt-2"># Stash changes temporarily</div>
                        <div>git stash</div>
                        <div>git stash pop</div>
                        <div className="mt-2"># Delete local branch</div>
                        <div>git branch -d branch_name</div>
                        <div className="mt-2"># Delete remote branch</div>
                        <div>git push origin --delete branch_name</div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-400 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-yellow-800 mb-4">⚠️ Best Practices</h3>
                      <ul className="space-y-3 text-gray-800">
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Always pull before creating new branches</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Write clear, descriptive commit messages</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Commit frequently with logical chunks of work</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Keep feature branches up to date with parent branches</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Test thoroughly before creating merge requests</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Delete branches after successful merge</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Use --no-ff flag for merges to preserve branch history</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-yellow-600 font-bold mr-2">✓</span>
                          <span>Never force push to shared branches (develop, sandbox, main)</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-400 rounded-lg p-6 shadow-md">
                      <h3 className="text-2xl font-bold text-red-800 mb-4">🚫 Common Mistakes to Avoid</h3>
                      <ul className="space-y-3 text-gray-800">
                        <li className="flex items-start">
                          <span className="text-red-600 font-bold mr-2">✗</span>
                          <span>Working directly on develop, sandbox, or main branches</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-600 font-bold mr-2">✗</span>
                          <span>Creating branches from outdated parent branches</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-600 font-bold mr-2">✗</span>
                          <span>Merging without testing or code review</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-600 font-bold mr-2">✗</span>
                          <span>Committing large, unrelated changes together</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-600 font-bold mr-2">✗</span>
                          <span>Forgetting to push commits to remote</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-red-600 font-bold mr-2">✗</span>
                          <span>Using incorrect version numbering for releases</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 p-6 border-t">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Quick Reference Card</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded shadow">
                  <h4 className="font-bold text-blue-600 mb-2">For Developers</h4>
                  <p className="text-sm text-gray-600">Work on feature branches, create MRs to module branches</p>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h4 className="font-bold text-purple-600 mb-2">For Module Leads</h4>
                  <p className="text-sm text-gray-600">Review feature MRs, merge module to develop</p>
                </div>
                <div className="bg-white p-4 rounded shadow">
                  <h4 className="font-bold text-green-600 mb-2">For Release Manager</h4>
                  <p className="text-sm text-gray-600">Create releases from sandbox, merge to main after testing</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-gray-600">
          <p className="text-sm">Git Branching Strategy Documentation v1.0</p>
          <p className="text-xs mt-1">Generated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

export default GitBranchingDocs;